import { Input, message, Spin, Table } from "antd";
import { CloseOutlined, CopyOutlined } from "@ant-design/icons";
import { useEffect, useState } from "react";
import { serverFetch } from "@server";
import { fmtDate } from "@extensions/convert";

const panelWidth = 800;

const buildClientLink = (order) => {
  if (!order?.id) return "";
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const guid = (order.orderGuid || "").replace(/\{|\}|-/g, "").toLowerCase();
  return `${origin}/doc/${guid}-${order.id}`;
};

const Order = ({ orderId, code, onClose, standalone = false }) => {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [previewImgUrl, setPreviewImgUrl] = useState(null);

  useEffect(() => {
    if (code) {
      setLoading(true);
      setOrder(null);
      serverFetch(`doc/${encodeURIComponent(code)}`)
        .then((data) => {
          setOrder(data);
          setLoading(false);
        })
        .catch((e) => {
          setLoading(false);
          if (e?.status === 404) message.error("Документ не найден");
          else message.error(e?.userMessage || e?.message || "Ошибка загрузки документа");
          onClose?.();
        });
    } else if (orderId) {
      setLoading(true);
      setOrder(null);
      serverFetch(`Order/${orderId}`)
        .then((data) => {
          setOrder(data);
          setLoading(false);
        })
        .catch((e) => {
          setLoading(false);
          if (e?.status === 404) message.error("Заказ не найден");
          else if (e?.status === 403) message.error("Нет доступа к заказу");
          else message.error(e?.userMessage || e?.message || "Ошибка загрузки заказа");
          onClose?.();
        });
    }
  }, [orderId, code]);

  if (!orderId && !code) return null;

  const formatSum = (v) => (v != null && v !== "" ? Number(v).toLocaleString("ru-RU", { minimumFractionDigits: 0, maximumFractionDigits: 2 }) : "—");

  const itemColumns = [
    {
      dataIndex: "itemNo",
      title: "№",
      width: 56,
      render: (val) => (
        <span style={{ fontWeight: "bold", fontSize: 16, verticalAlign: "top" }}>{val ?? "—"}</span>
      ),
      onCell: () => ({ style: { verticalAlign: "top" } }),
    },
    {
      dataIndex: "img",
      title: "Фото",
      width: 300,
      render: (url) =>
        url ? (
          <div
            style={{
              width: 300,
              height: 300,
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "center",
            }}
          >
            <img
              src={url}
              style={{
                maxWidth: 300,
                maxHeight: 300,
                objectFit: "contain",
                cursor: "pointer",
              }}
              alt=""
              onClick={() => setPreviewImgUrl(url)}
            />
          </div>
        ) : "—",
    },
    {
      title: "Позиция",
      width: 260,
      render: (_, record) => {
        const { name, w, h, qty, syma } = record;
        const hasW = w != null && w !== "";
        const hasH = h != null && h !== "";
        const dimLabel = hasW && hasH ? "Габарит" : hasW ? "Длина" : null;
        const dimVal = hasW && hasH ? `${w} x ${h}` : hasW ? String(w) : null;
        const rows = [
          ["Наименование", name ?? "—"],
          ...(dimLabel ? [[dimLabel, dimVal]] : []),
          ["Количество", qty != null && qty !== "" ? qty : "—"],
          ["Стоимость", formatSum(syma)],
        ];
        return (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, verticalAlign: "top" }}>
            <tbody>
              {rows.map(([label, value], i) => (
                <tr key={i}>
                  <td style={{ padding: "2px 8px 2px 0", color: "#8c8c8c", whiteSpace: "nowrap", width: 1 }}>{label}</td>
                  <td style={{ padding: "2px 0" }}>{value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        );
      },
      onCell: () => ({ style: { verticalAlign: "top" } }),
    },
  ];

  const panel = (
    <div
      className="misoft-modal-content"
      style={{
        position: standalone ? "relative" : "fixed",
        top: standalone ? undefined : 0,
        right: standalone ? undefined : 0,
        width: panelWidth,
        maxWidth: "100vw",
        height: standalone ? "100vh" : "100vh",
        margin: 0,
        boxShadow: "-4px 0 16px rgba(0,0,0,0.15)",
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: "#fff",
      }}
      onClick={standalone ? undefined : (e) => e.stopPropagation()}
    >
        <div
          className="misoft-modal-title"
          style={{
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 16,
            paddingLeft: 16,
            paddingRight: 16,
          }}
        >
          <span style={{ flexShrink: 0 }}>{order ? `Заказ #${order.docNum ?? order.id}` : "Заказ"}</span>
          {!standalone && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flex: 1,
                minWidth: 0,
                justifyContent: "center",
              }}
            >
              <Input
                readOnly
                value={order ? buildClientLink(order) : ""}
                title="Ссылка для доступа к документу без авторизации"
                style={{ background: "#f5f5f5", flex: 1, maxWidth: 500 }}
                size="small"
              />
              <CopyOutlined
                style={{ fontSize: 16, cursor: "pointer", color: "#595959", flexShrink: 0 }}
                onClick={() => {
                  const url = order ? buildClientLink(order) : "";
                  if (url && navigator.clipboard?.writeText) {
                    navigator.clipboard.writeText(url).then(() => message.success("Ссылка скопирована в буфер обмена"));
                  }
                }}
              />
            </div>
          )}
          {onClose && (
            <CloseOutlined
              className="misoft-section-title-close"
              onClick={onClose}
              style={{ zIndex: 9, color: "#2f6f00", fontSize: 16, flexShrink: 0 }}
            />
          )}
        </div>
        <div style={{ flex: 1, overflow: "auto", padding: 16 }}>
          {loading && (
            <div style={{ display: "flex", justifyContent: "center", padding: 48 }}>
              <Spin />
            </div>
          )}
          {!loading && order && (
            <>
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 1,
                marginBottom: 16,
                border: "1px solid #f0f0f0",
                borderRadius: 4,
                overflow: "hidden",
              }}>
                {[
                  ["Дата документа", fmtDate(order.docDate)],
                  ["Номер документа", order.docNum ?? "—"],
                  ["Статус", order.status ?? "—"],
                  ["Отдел", order.depNo ?? "—"],
                  ["Клиент", order.predName ?? "—"],
                  ["Адрес", order.address ?? "—"],
                  ["Телефон", order.phone ?? "—"],
                  ["Дата готовности", fmtDate(order.finDate)],
                  ["Площадь", order.area != null ? Number(order.area) : "—"],
                  ["Сумма", order.syma != null ? Number(order.syma) : "—"],
                  ["Долг", order.debet != null ? Number(order.debet) : "—"],
                ].map(([label, value], i) => (
                  <div
                    key={i}
                    style={{
                      position: "relative",
                      padding: "6px 8px",
                      paddingTop: 18,
                      background: "#fafafa",
                      borderBottom: "1px solid #f0f0f0",
                      borderRight: i % 2 === 0 ? "1px solid #f0f0f0" : undefined,
                      fontSize: 13,
                    }}
                  >
                    <span style={{ position: "absolute", top: 4, left: 8, fontSize: 10, color: "#8c8c8c" }}>
                      {label}
                    </span>
                    <span style={{ wordBreak: "break-word" }}>{value}</span>
                  </div>
                ))}
              </div>
              {order.items && order.items.length > 0 && (
                <Table
                  dataSource={order.items}
                  columns={itemColumns}
                  rowKey="id"
                  pagination={false}
                  size="small"
                  scroll={{ x: "max-content" }}
                />
              )}
            </>
          )}
        </div>
      </div>
  );

  const previewOverlay = previewImgUrl && (
    <div
      role="button"
      tabIndex={0}
      onClick={() => setPreviewImgUrl(null)}
      onKeyDown={(e) => e.key === "Escape" && setPreviewImgUrl(null)}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2000,
        background: "rgba(0,0,0,0.75)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        cursor: "pointer",
      }}
    >
      <img
        src={previewImgUrl}
        alt=""
        style={{ maxWidth: "90vw", maxHeight: "90vh", objectFit: "contain" }}
        onClick={(e) => e.stopPropagation()}
      />
    </div>
  );

  if (standalone) {
    return (
      <div style={{ minHeight: "100vh", display: "flex", justifyContent: "center", background: "#f0f2f5" }}>
        <div style={{ width: "100%", maxWidth: panelWidth }}>{panel}</div>
        {previewOverlay}
      </div>
    );
  }

  return (
    <div
      className="misoft-modal-overlay"
      onClick={(e) => e.target === e.currentTarget && onClose?.()}
      style={{ zIndex: 1050 }}
    >
      {panel}
      {previewOverlay}
    </div>
  );
};

export default Order;
